import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Search,
    Calendar,
    Package,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight,
    Filter,
    X,
    ChevronDown,
    Check,
    ArrowRightLeft,
    Layers,
    Users,
    TrendingUp,
    Truck,
    Building2,
    Box,
    Trash2,
    AlertCircle
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';
import PhanBoBaoBiEmTongThe from './PhanBoBaoBiEmTongThe';
import ChuyenBaoBiAnhSangEm from './ChuyenBaoBiAnhSangEm';
import NhapPallet from './NhapPallet';

// ================== Utils ==================
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : Math.max(0, num);
};

const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

const formatNumberWithDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    // Nếu là số nguyên, hiển thị không có phần thập phân
    // Nếu là số thập phân, hiển thị với phần thập phân
    if (Number.isInteger(num)) {
        return num.toLocaleString('vi-VN');
    }
    return num.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 10 });
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ================== Pagination ==================
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex justify-center items-center gap-2 mt-4">
            <button
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                className={`p-2 rounded-md ${currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
            >
                <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600">
                Trang {currentPage} / {totalPages || 1}
            </span>
            <button
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                className={`p-2 rounded-md ${currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-indigo-600 hover:bg-indigo-50'
                    }`}
            >
                <ChevronRight className="w-5 h-5" />
            </button>
        </div>
    );
};

const SortIcon = ({ sortConfig, column }) => {
    if (sortConfig.key !== column) {
        return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc'
        ? <ArrowUp className="w-4 h-4 text-indigo-600" />
        : <ArrowDown className="w-4 h-4 text-indigo-600" />;
};

// ================== Statistics Card ==================
const StatisticsCard = ({ title, value, icon: Icon, color = 'indigo', subtitle }) => {
    const colorClasses = {
        indigo: 'bg-indigo-50 text-indigo-600',
        pink: 'bg-pink-50 text-pink-600',
        green: 'bg-green-50 text-green-600',
        orange: 'bg-orange-50 text-orange-600',
        teal: 'bg-teal-50 text-teal-600',
        purple: 'bg-purple-50 text-purple-600',
        blue: 'bg-blue-50 text-blue-600',
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                </div>
                {Icon && (
                    <div className={`p-3 rounded-lg ${colorClasses[color] || colorClasses.indigo}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                )}
            </div>
        </div>
    );
};

// ================== Component chính ==================
const PhanBoBaoBiManagement = () => {
    const location = useLocation();

    const [phanBoData, setPhanBoData] = useState([]);   // bảng PHANBOBAOBI
    const [nhapBaoBiMap, setNhapBaoBiMap] = useState({}); // map ID -> record NHAPBAOBI
    const [chuyenBaoBiData, setChuyenBaoBiData] = useState([]); // bảng CHUYENBAOBI
    const [palletData, setPalletData] = useState([]); // bảng NHAPBAOBI_CT
    const [viewTab, setViewTab] = useState('anh'); // 'anh' | 'em' | 'chuyen' | 'pallet'

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
    });
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: 'NGÀY',
        direction: 'desc'
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);

    // Modal states
    const [showPhanBoEmModal, setShowPhanBoEmModal] = useState(false);
    const [showChuyenBaoBiModal, setShowChuyenBaoBiModal] = useState(false);
    const [showNhapPalletModal, setShowNhapPalletModal] = useState(false);

    // lọc theo TỔ (multi-select)
    const [selectedToList, setSelectedToList] = useState([]);
    const [showToDropdown, setShowToDropdown] = useState(false);
    const toDropdownRef = useRef(null);

    // lọc theo NGHIỆP VỤ (cho tab Pallet)
    const [selectedNghiepVuList, setSelectedNghiepVuList] = useState([]);
    const [showNghiepVuDropdown, setShowNghiepVuDropdown] = useState(false);
    const nghiepVuDropdownRef = useRef(null);

    // State cho modal confirm xóa
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // ====== Click outside để đóng dropdown ======
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (toDropdownRef.current && !toDropdownRef.current.contains(e.target)) {
                setShowToDropdown(false);
            }
            if (nghiepVuDropdownRef.current && !nghiepVuDropdownRef.current.contains(e.target)) {
                setShowNghiepVuDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ====== Xử lý tab từ location state ======
    useEffect(() => {
        if (location.state?.tab) {
            const validTabs = ['anh', 'em', 'chuyen', 'pallet'];
            if (validTabs.includes(location.state.tab)) {
                setViewTab(location.state.tab);
            }
        }
    }, [location]);

    // ====== Hàm fetch data ======
    const fetchAllData = async () => {
        try {
            const [phanBoRes, nhapRes, chuyenRes, palletRes] = await Promise.all([
                authUtils.apiRequest('PHANBOBAOBI', 'Find', {}),
                authUtils.apiRequest('NHAPBAOBI', 'Find', {}),
                authUtils.apiRequest('CHUYENBAOBI', 'Find', {}),
                authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {}),
            ]);

            setPhanBoData(phanBoRes || []);
            setChuyenBaoBiData(chuyenRes || []);
            setPalletData(palletRes || []);

            // xây map ID -> record NHAPBAOBI
            const map = {};
            (nhapRes || []).forEach(row => {
                if (row.ID) {
                    map[row.ID] = row;
                }
            });
            setNhapBaoBiMap(map);
        } catch (err) {
            console.error('Error fetching data:', err);
            toast.error('Lỗi khi tải dữ liệu phân bổ / nhập bao bì');
        }
    };

    // ====== Gọi API PHANBOBAOBI + NHAPBAOBI + CHUYENBAOBI + NHAPBAOBI_CT ======
    useEffect(() => {
        fetchAllData();
    }, []);

    // ====== Hàm xóa PHANBOBAOBI ======
    const handleDeletePhanBo = (row) => {
        // Chỉ cho phép xóa loại "phanbo", không cho xóa loại "chuyen_tru" hoặc "chuyen_cong"
        if (row.type !== 'phanbo') {
            toast.warning('Không thể xóa dòng chuyển bao bì từ đây. Vui lòng xóa từ tab Chuyển bao bì.');
            return;
        }

        setConfirmTitle('Xóa phân bổ');
        setConfirmMessage(`Bạn có chắc chắn muốn xóa phân bổ này?\nTổ: ${row['TỔ']}\nKhối lượng: ${formatNumber(row['KHỐI LƯỢNG'])} KG`);
        setConfirmAction(() => async () => {
            try {
                setIsDeleting(true);

                // Xóa NHAPBAOBI_CT theo IDPB_REF trước
                const nhapBaoBiCTResponse = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});
                const nhapBaoBiCTToDelete = nhapBaoBiCTResponse.filter(ct => ct.IDPB_REF === row.IDPB);
                if (nhapBaoBiCTToDelete.length > 0) {
                    await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                        Rows: nhapBaoBiCTToDelete.map(ct => ({ ID_CT: ct.ID_CT }))
                    });
                }

                // Xóa PHANBOBAOBI
                await authUtils.apiRequest('PHANBOBAOBI', 'Delete', {
                    Rows: [{ IDPB: row.IDPB }]
                });

                toast.success('Xóa phân bổ thành công!');
                await fetchAllData();
            } catch (error) {
                console.error('Error deleting phanbo:', error);
                toast.error('Có lỗi xảy ra khi xóa phân bổ');
            } finally {
                setIsDeleting(false);
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    // ====== Hàm xóa CHUYENBAOBI ======
    const handleDeleteChuyenBaoBi = (row) => {
        setConfirmTitle('Xóa chuyển bao bì');
        setConfirmMessage(`Bạn có chắc chắn muốn xóa chuyển bao bì này?\nTừ: ${row['TỔ CHUYỂN']} → ${row['TỔ NHẬN']}\nKhối lượng: ${formatNumber(row['KHỐI LƯỢNG'])} KG`);
        setConfirmAction(() => async () => {
            try {
                setIsDeleting(true);

                // Xóa NHAPBAOBI_CT theo IDCBB_REF trước
                const nhapBaoBiCTResponse = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});
                const nhapBaoBiCTToDelete = nhapBaoBiCTResponse.filter(ct => ct.IDCBB_REF === row.IDCBB);
                if (nhapBaoBiCTToDelete.length > 0) {
                    await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                        Rows: nhapBaoBiCTToDelete.map(ct => ({ ID_CT: ct.ID_CT }))
                    });
                }

                // Xóa CHUYENBAOBI
                await authUtils.apiRequest('CHUYENBAOBI', 'Delete', {
                    Rows: [{ IDCBB: row.IDCBB }]
                });

                toast.success('Xóa chuyển bao bì thành công!');
                await fetchAllData();
            } catch (error) {
                console.error('Error deleting chuyenbaobi:', error);
                toast.error('Có lỗi xảy ra khi xóa chuyển bao bì');
            } finally {
                setIsDeleting(false);
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    // ====== Hàm xóa Pallet (NHAPBAOBI_CT) ======
    const handleDeletePallet = (row) => {
        setConfirmTitle('Xóa dữ liệu Pallet');
        setConfirmMessage(`Bạn có chắc chắn muốn xóa dữ liệu Pallet này?\nTổ: ${row['TỔ']}\nNghiệp vụ: ${row['NGHIỆP VỤ']}\nSố lượng: ${formatNumber(row['KHỐI LƯỢNG (KG)'])} cái`);
        setConfirmAction(() => async () => {
            try {
                setIsDeleting(true);

                // Xóa NHAPBAOBI_CT
                await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                    Rows: [{ ID_CT: row.ID_CT }]
                });

                toast.success('Xóa dữ liệu Pallet thành công!');
                await fetchAllData();
            } catch (error) {
                console.error('Error deleting pallet:', error);
                toast.error('Có lỗi xảy ra khi xóa dữ liệu Pallet');
            } finally {
                setIsDeleting(false);
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleFilterDateChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value || null
        }));
        setCurrentPage(1);
    };

    // ====== Tạo dữ liệu tổng hợp cho tab Anh và Em (bao gồm cả chuyển đổi) ======
    const combinedData = useMemo(() => {
        const result = [];

        // Thêm dữ liệu từ PHANBOBAOBI
        phanBoData.forEach(row => {
            const tenHang = String(row['TÊN HÀNG'] || '').trim().toLowerCase();
            result.push({
                ...row,
                type: 'phanbo',
                loai: tenHang === 'bao bì anh' ? 'anh' : 'em'
            });
        });

        // Thêm dữ liệu chuyển đổi (tạo 2 dòng: trừ từ bên chuyển, cộng vào bên nhận)
        chuyenBaoBiData.forEach(row => {
            const nghiepVu = String(row['NGHIỆP VỤ'] || '').trim();
            const isAnhSangEm = nghiepVu === 'Anh sang Em';
            const isAnhSangAnh = nghiepVu === 'Anh sang Anh';
            const isEmSangEm = nghiepVu === 'Em sang Em';

            if (isEmSangEm) {
                // Em sang Em: cả 2 dòng đều là bao bì em
                result.push({
                    NGÀY: row['NGÀY'],
                    TỔ: row['TỔ CHUYỂN'],
                    'TÊN HÀNG': 'Bao bì em',
                    'SỐ LỒNG': row['SỐ LỒNG'],
                    'KHỐI LƯỢNG THEO LỒNG': parseNumber(row['KHỐI LƯỢNG/LỒNG EM']),
                    'KHỐI LƯỢNG': parseNumber(row['KHỐI LƯỢNG']),
                    type: 'chuyen_tru',
                    loai: 'em',
                    chuyenData: row,
                    IDPB: `chuyen_tru_${row.IDCBB}`
                });
                result.push({
                    NGÀY: row['NGÀY'],
                    TỔ: row['TỔ NHẬN'],
                    'TÊN HÀNG': 'Bao bì em',
                    'SỐ LỒNG': row['SỐ LỒNG QUY ĐỔI'],
                    'KHỐI LƯỢNG THEO LỒNG': parseNumber(row['KHỐI LƯỢNG/LỒNG EM']),
                    'KHỐI LƯỢNG': parseNumber(row['KHỐI LƯỢNG']),
                    type: 'chuyen_cong',
                    loai: 'em',
                    chuyenData: row,
                    IDPB: `chuyen_cong_${row.IDCBB}`
                });
                return;
            }

            // Dòng trừ từ Bao bì anh (Tổ chuyển)
            result.push({
                NGÀY: row['NGÀY'],
                TỔ: row['TỔ CHUYỂN'],
                'TÊN HÀNG': 'Bao bì anh',
                'SỐ LỒNG': row['SỐ LỒNG'],
                'KHỐI LƯỢNG': parseNumber(row['KHỐI LƯỢNG']),
                type: 'chuyen_tru',
                loai: 'anh',
                chuyenData: row,
                IDPB: `chuyen_tru_${row.IDCBB}`
            });

            // Dòng cộng vào (Tổ nhận) - phụ thuộc vào nghiệp vụ
            if (isAnhSangEm) {
                result.push({
                    NGÀY: row['NGÀY'],
                    TỔ: row['TỔ NHẬN'],
                    'TÊN HÀNG': 'Bao bì em',
                    'SỐ LỒNG': row['SỐ LỒNG QUY ĐỔI'],
                    'KHỐI LƯỢNG THEO LỒNG': parseNumber(row['KHỐI LƯỢNG/LỒNG EM']),
                    'KHỐI LƯỢNG': parseNumber(row['KHỐI LƯỢNG']),
                    type: 'chuyen_cong',
                    loai: 'em',
                    chuyenData: row,
                    IDPB: `chuyen_cong_${row.IDCBB}`
                });
            } else if (isAnhSangAnh) {
                result.push({
                    NGÀY: row['NGÀY'],
                    TỔ: row['TỔ NHẬN'],
                    'TÊN HÀNG': 'Bao bì anh',
                    'SỐ LỒNG': 0,
                    'KHỐI LƯỢNG THEO LỒNG': 0,
                    'KHỐI LƯỢNG': parseNumber(row['KHỐI LƯỢNG']),
                    type: 'chuyen_cong',
                    loai: 'anh',
                    chuyenData: row,
                    IDPB: `chuyen_cong_${row.IDCBB}`
                });
            }
        });

        return result;
    }, [phanBoData, chuyenBaoBiData]);

    // ====== Danh sách TỔ theo tab ======
    const toOptions = useMemo(() => {
        if (viewTab === 'chuyen') {
            // Tab chuyển: lấy cả TỔ CHUYỂN và TỔ NHẬN
            const setTo = new Set();
            chuyenBaoBiData.forEach(r => {
                if (r['TỔ CHUYỂN']) setTo.add(String(r['TỔ CHUYỂN']).trim());
                if (r['TỔ NHẬN']) setTo.add(String(r['TỔ NHẬN']).trim());
            });
            return Array.from(setTo).sort();
        } else if (viewTab === 'pallet') {
            // Tab pallet: lấy TỔ từ palletData
            const setTo = new Set();
            palletData.forEach(r => {
                if (r['TỔ']) setTo.add(String(r['TỔ']).trim());
            });
            return Array.from(setTo).sort();
        } else {
            // Tab Anh/Em
            const dataForTab = combinedData.filter(row => row.loai === viewTab);
            const setTo = new Set();
            dataForTab.forEach(r => {
                if (r['TỔ']) setTo.add(String(r['TỔ']).trim());
            });
            return Array.from(setTo).sort();
        }
    }, [combinedData, chuyenBaoBiData, palletData, viewTab]);

    // ====== Danh sách NGHIỆP VỤ cho tab Pallet ======
    const nghiepVuOptions = useMemo(() => {
        if (viewTab !== 'pallet') return [];
        const setNV = new Set();
        palletData.forEach(r => {
            if (r['NGHIỆP VỤ']) setNV.add(String(r['NGHIỆP VỤ']).trim());
        });
        return Array.from(setNV).sort();
    }, [palletData, viewTab]);

    useEffect(() => {
        setSelectedToList([]);
        setSelectedNghiepVuList([]);
        setCurrentPage(1);
    }, [viewTab]);

    // ====== Filter + sort ======
    const filteredData = useMemo(() => {
        let data = [];

        if (viewTab === 'pallet') {
            // Tab Pallet: hiển thị trực tiếp từ NHAPBAOBI_CT và lọc TÊN HÀNG = "Pallet"
            data = palletData.filter(row => {
                const tenHang = String(row['TÊN HÀNG'] || '').trim().toLowerCase();
                return tenHang === 'pallet';
            });

            // Lọc theo TỔ
            if (selectedToList.length > 0) {
                data = data.filter(row =>
                    selectedToList.includes(String(row['TỔ'] || '').trim())
                );
            }

            // Lọc theo NGHIỆP VỤ
            if (selectedNghiepVuList.length > 0) {
                data = data.filter(row =>
                    selectedNghiepVuList.includes(String(row['NGHIỆP VỤ'] || '').trim())
                );
            }

            // Search
            if (search.trim()) {
                const keyword = search.trim().toLowerCase();
                data = data.filter(row => {
                    const to = String(row['TỔ'] || '').toLowerCase();
                    const tenHang = String(row['TÊN HÀNG'] || '').toLowerCase();
                    const nghiepVu = String(row['NGHIỆP VỤ'] || '').toLowerCase();

                    return (
                        to.includes(keyword) ||
                        tenHang.includes(keyword) ||
                        nghiepVu.includes(keyword)
                    );
                });
            }

            // Lọc theo ngày
            if (filters.startDate || filters.endDate) {
                data = data.filter(row => {
                    const dateStr = formatDateForInput(row['NGÀY']);
                    if (!dateStr) return false;

                    const recordDate = new Date(dateStr);
                    recordDate.setHours(0, 0, 0, 0);

                    if (filters.startDate) {
                        const start = new Date(filters.startDate);
                        start.setHours(0, 0, 0, 0);
                        if (recordDate < start) return false;
                    }
                    if (filters.endDate) {
                        const end = new Date(filters.endDate);
                        end.setHours(23, 59, 59, 999);
                        if (recordDate > end) return false;
                    }
                    return true;
                });
            }

            // Sort
            if (sortConfig.key) {
                data = [...data].sort((a, b) => {
                    let aVal = a[sortConfig.key];
                    let bVal = b[sortConfig.key];

                    if (sortConfig.key === 'NGÀY') {
                        aVal = new Date(aVal);
                        bVal = new Date(bVal);
                    } else if (sortConfig.key === 'KHỐI LƯỢNG (KG)') {
                        aVal = parseNumber(aVal);
                        bVal = parseNumber(bVal);
                    } else {
                        aVal = String(aVal || '').toLowerCase();
                        bVal = String(bVal || '').toLowerCase();
                    }

                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                });
            }

        } else if (viewTab === 'chuyen') {
            // Tab chuyển: hiển thị trực tiếp từ CHUYENBAOBI
            data = [...chuyenBaoBiData];

            // Lọc theo TỔ
            if (selectedToList.length > 0) {
                data = data.filter(row =>
                    selectedToList.includes(String(row['TỔ CHUYỂN'] || '').trim()) ||
                    selectedToList.includes(String(row['TỔ NHẬN'] || '').trim())
                );
            }

            // Search
            if (search.trim()) {
                const keyword = search.trim().toLowerCase();
                data = data.filter(row => {
                    const toChuyen = String(row['TỔ CHUYỂN'] || '').toLowerCase();
                    const toNhan = String(row['TỔ NHẬN'] || '').toLowerCase();
                    const soLong = String(row['SỐ LỒNG'] || '').toLowerCase();
                    const ghiChu = String(row['GHI CHÚ'] || '').toLowerCase();

                    return (
                        toChuyen.includes(keyword) ||
                        toNhan.includes(keyword) ||
                        soLong.includes(keyword) ||
                        ghiChu.includes(keyword)
                    );
                });
            }

            // Lọc theo ngày
            if (filters.startDate || filters.endDate) {
                data = data.filter(row => {
                    const dateStr = formatDateForInput(row['NGÀY']);
                    if (!dateStr) return false;

                    const recordDate = new Date(dateStr);
                    recordDate.setHours(0, 0, 0, 0);

                    if (filters.startDate) {
                        const start = new Date(filters.startDate);
                        start.setHours(0, 0, 0, 0);
                        if (recordDate < start) return false;
                    }
                    if (filters.endDate) {
                        const end = new Date(filters.endDate);
                        end.setHours(23, 59, 59, 999);
                        if (recordDate > end) return false;
                    }
                    return true;
                });
            }

            // Sort
            if (sortConfig.key) {
                data = [...data].sort((a, b) => {
                    let aVal = a[sortConfig.key];
                    let bVal = b[sortConfig.key];

                    if (sortConfig.key === 'NGÀY') {
                        aVal = new Date(aVal);
                        bVal = new Date(bVal);
                    } else if (
                        sortConfig.key === 'KHỐI LƯỢNG' ||
                        sortConfig.key === 'KHỐI LƯỢNG/LỒNG CD' ||
                        sortConfig.key === 'KHỐI LƯỢNG/LỒNG EM' ||
                        sortConfig.key === 'SỐ LỒNG QUY ĐỔI'
                    ) {
                        aVal = parseNumber(aVal);
                        bVal = parseNumber(bVal);
                    } else {
                        aVal = String(aVal || '').toLowerCase();
                        bVal = String(bVal || '').toLowerCase();
                    }

                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                });
            }

        } else {
            // Tab Anh hoặc Em: dùng combinedData
            data = combinedData.filter(row => row.loai === viewTab);

            // Lọc theo TỔ
            if (selectedToList.length > 0) {
                data = data.filter(row =>
                    selectedToList.includes(String(row['TỔ'] || '').trim())
                );
            }

            // Search
            if (search.trim()) {
                const keyword = search.trim().toLowerCase();
                data = data.filter(row => {
                    const toStr = String(row['TỔ'] || '').toLowerCase();
                    const soDay = String(row['SỐ DÂY'] || '').toLowerCase();
                    const soLong = String(row['SỐ LỒNG'] || '').toLowerCase();
                    const idNhap = String(row['ID_NHAPBAOBI'] || '').toLowerCase();

                    // lấy thêm SỐ XE, NCC từ bảng nhập bao bì nếu có
                    const nhap = nhapBaoBiMap[row.ID_NHAPBAOBI] || {};
                    const soXe = String(nhap['SỐ XE'] || '').toLowerCase();
                    const ncc = String(nhap['NCC'] || '').toLowerCase();

                    if (viewTab === 'anh') {
                        return (
                            toStr.includes(keyword) ||
                            soDay.includes(keyword) ||
                            soLong.includes(keyword) ||
                            idNhap.includes(keyword) ||
                            soXe.includes(keyword) ||
                            ncc.includes(keyword)
                        );
                    }

                    // Bao bì em
                    return (
                        toStr.includes(keyword) ||
                        soLong.includes(keyword)
                    );
                });
            }

            // Lọc theo ngày
            if (filters.startDate || filters.endDate) {
                data = data.filter(row => {
                    const dateStr = formatDateForInput(row['NGÀY']);
                    if (!dateStr) return false;

                    const recordDate = new Date(dateStr);
                    recordDate.setHours(0, 0, 0, 0);

                    if (filters.startDate) {
                        const start = new Date(filters.startDate);
                        start.setHours(0, 0, 0, 0);
                        if (recordDate < start) return false;
                    }
                    if (filters.endDate) {
                        const end = new Date(filters.endDate);
                        end.setHours(23, 59, 59, 999);
                        if (recordDate > end) return false;
                    }
                    return true;
                });
            }

            // Sort
            if (sortConfig.key) {
                data = [...data].sort((a, b) => {
                    let aVal = a[sortConfig.key];
                    let bVal = b[sortConfig.key];

                    if (sortConfig.key === 'NGÀY') {
                        aVal = new Date(aVal);
                        bVal = new Date(bVal);
                    } else if (
                        sortConfig.key === 'KHỐI LƯỢNG' ||
                        sortConfig.key === 'KHỐI LƯỢNG THEO LỒNG'
                    ) {
                        aVal = parseNumber(aVal);
                        bVal = parseNumber(bVal);
                    } else {
                        aVal = String(aVal || '').toLowerCase();
                        bVal = String(bVal || '').toLowerCase();
                    }

                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                });
            }
        }

        return data;
    }, [combinedData, chuyenBaoBiData, palletData, viewTab, search, filters, sortConfig, selectedToList, selectedNghiepVuList, nhapBaoBiMap]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
    const currentItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return filteredData.slice(start, end);
    }, [filteredData, currentPage, itemsPerPage]);

    // Tổng khối lượng
    const totalKhoiLuong = useMemo(
        () => {
            if (viewTab === 'pallet') {
                return filteredData.reduce((s, r) => s + parseNumber(r['KHỐI LƯỢNG (KG)']), 0);
            }
            return filteredData.reduce((s, r) => {
                const kl = parseNumber(r['KHỐI LƯỢNG']);
                return r.type === 'chuyen_tru' ? s - kl : s + kl;
            }, 0);
        },
        [filteredData, viewTab]
    );

    const totalKhoiLuongTheoLong = useMemo(
        () => {
            if (viewTab === 'em') {
                return filteredData.reduce((s, r) => {
                    const kl = parseNumber(r['KHỐI LƯỢNG THEO LỒNG']);
                    return r.type === 'chuyen_tru' ? s - kl : s + kl;
                }, 0);
            }
            if (viewTab === 'chuyen') {
                return filteredData.reduce((s, r) => s + parseNumber(r['KHỐI LƯỢNG/LỒNG EM']), 0);
            }
            return 0;
        },
        [filteredData, viewTab]
    );

    const totalKhoiLuongCD = useMemo(
        () => viewTab === 'chuyen'
            ? filteredData.reduce((s, r) => s + parseNumber(r['KHỐI LƯỢNG/LỒNG CD']), 0)
            : 0,
        [filteredData, viewTab]
    );

    // ====== Statistics cho từng tab ======
    const statistics = useMemo(() => {
        if (viewTab === 'anh') {
            // Tab Bao bì anh: Tổng khối lượng, số tổ, số xe, NCC
            const uniqueTo = new Set();
            const uniqueSoXe = new Set();
            const uniqueNCC = new Set();

            filteredData.forEach(row => {
                if (row['TỔ']) uniqueTo.add(String(row['TỔ']).trim());

                // Lấy thông tin từ nhapBaoBiMap
                const nhap = nhapBaoBiMap[row.ID_NHAPBAOBI] || {};
                if (nhap['SỐ XE']) uniqueSoXe.add(String(nhap['SỐ XE']).trim());
                if (nhap['NCC']) uniqueNCC.add(String(nhap['NCC']).trim());
            });

            return {
                totalKhoiLuong,
                countTo: uniqueTo.size,
                countSoXe: uniqueSoXe.size,
                countNCC: uniqueNCC.size,
            };
        } else if (viewTab === 'em') {
            // Tab Bao bì em: Tổng số lồng, tổng khối lượng, tổng tổ
            const uniqueTo = new Set();
            let totalSoLong = 0;

            filteredData.forEach(row => {
                if (row['TỔ']) uniqueTo.add(String(row['TỔ']).trim());

                // Tính tổng số lồng
                const soLong = parseFloat(row['SỐ LỒNG']) || 0;
                totalSoLong += soLong;
            });

            return {
                totalSoLong,
                totalKhoiLuong,
                totalKhoiLuongTheoLong,
                countTo: uniqueTo.size,
            };
        } else if (viewTab === 'chuyen') {
            // Tab Chuyển: Số lồng chuyển, khối lượng chuyển, số lồng nhận
            let totalSoLongChuyen = 0;
            let totalSoLongNhan = 0;

            filteredData.forEach(row => {
                const soLong = parseFloat(row['SỐ LỒNG']) || 0;
                const soLongQuyDoi = parseFloat(row['SỐ LỒNG QUY ĐỔI']) || 0;

                totalSoLongChuyen += soLong;
                totalSoLongNhan += soLongQuyDoi;
            });

            return {
                totalSoLongChuyen,
                totalKhoiLuong,
                totalSoLongNhan,
            };
        } else if (viewTab === 'pallet') {
            // Tab Pallet: Tổng đóng, tổng sửa, tổng
            let totalDong = 0;
            let totalSua = 0;

            filteredData.forEach(row => {
                const nghiepVu = String(row['NGHIỆP VỤ'] || '').trim().toLowerCase();
                const khoiLuong = parseNumber(row['KHỐI LƯỢNG (KG)']);

                if (nghiepVu.startsWith('đóng')) {
                    totalDong += khoiLuong;
                } else if (nghiepVu.startsWith('sửa')) {
                    totalSua += khoiLuong;
                }
            });

            return {
                totalDong,
                totalSua,
                total: totalKhoiLuong,
            };
        }

        return {};
    }, [viewTab, filteredData, totalKhoiLuong, totalKhoiLuongTheoLong, nhapBaoBiMap]);

    // Handler TỔ
    const toggleToOption = (value) => {
        setSelectedToList(prev => {
            if (prev.includes(value)) {
                return prev.filter(v => v !== value);
            }
            return [...prev, value];
        });
        setCurrentPage(1);
    };

    // Handler NGHIỆP VỤ
    const toggleNghiepVuOption = (value) => {
        setSelectedNghiepVuList(prev => {
            if (prev.includes(value)) {
                return prev.filter(v => v !== value);
            }
            return [...prev, value];
        });
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({ startDate: null, endDate: null });
        setSearch('');
        setSelectedToList([]);
        setSelectedNghiepVuList([]);
        setCurrentPage(1);
    };

    return (
        <div className="p-3 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                Chi tiết phân bổ bao bì
                            </h1>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(v => !v)}
                                className="px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? 'Ẩn bộ lọc' : 'Bộ lọc'}
                            </button>

                            {viewTab === 'em' && (
                                <button
                                    onClick={() => setShowPhanBoEmModal(true)}
                                    className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center gap-2 transition-colors shadow-sm text-sm"
                                >
                                    <Package className="w-4 h-4" />
                                    Phân bổ bao bì em
                                </button>
                            )}

                            {viewTab === 'chuyen' && (
                                <button
                                    onClick={() => setShowChuyenBaoBiModal(true)}
                                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 transition-colors shadow-sm text-sm"
                                >
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Chuyển bao bì
                                </button>
                            )}

                            {viewTab === 'pallet' && (
                                <button
                                    onClick={() => setShowNhapPalletModal(true)}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 transition-colors shadow-sm text-sm"
                                >
                                    <Layers className="w-4 h-4" />
                                    Nhập dữ liệu Pallet
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
                        <button
                            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${viewTab === 'anh'
                                ? 'border-indigo-600 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => {
                                setViewTab('anh');
                                setCurrentPage(1);
                            }}
                        >
                            <Package className="w-4 h-4" />
                            Bao bì anh
                        </button>
                        <button
                            className={`ml-4 px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${viewTab === 'em'
                                ? 'border-pink-500 text-pink-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => {
                                setViewTab('em');
                                setCurrentPage(1);
                            }}
                        >
                            <Package className="w-4 h-4" />
                            Bao bì em
                        </button>
                        <button
                            className={`ml-4 px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${viewTab === 'chuyen'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => {
                                setViewTab('chuyen');
                                setCurrentPage(1);
                            }}
                        >
                            <ArrowRightLeft className="w-4 h-4" />
                            Chuyển bao bì
                        </button>
                        <button
                            className={`ml-4 px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${viewTab === 'pallet'
                                ? 'border-teal-500 text-teal-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => {
                                setViewTab('pallet');
                                setCurrentPage(1);
                            }}
                        >
                            <Layers className="w-4 h-4" />
                            Pallet
                        </button>
                    </div>

                    {/* Statistics Cards */}
                    {viewTab === 'anh' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <StatisticsCard
                                title="Tổng khối lượng"
                                value={`${formatNumber(statistics.totalKhoiLuong)} KG`}
                                icon={TrendingUp}
                                color="indigo"
                            />
                            <StatisticsCard
                                title="Số tổ"
                                value={statistics.countTo}
                                icon={Users}
                                color="green"
                            />
                            <StatisticsCard
                                title="Số xe"
                                value={statistics.countSoXe}
                                icon={Truck}
                                color="orange"
                            />
                            <StatisticsCard
                                title="Số NCC"
                                value={statistics.countNCC}
                                icon={Building2}
                                color="purple"
                            />
                        </div>
                    )}

                    {viewTab === 'em' && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <StatisticsCard
                                    title="Tổng số lồng"
                                    value={formatNumberWithDecimal(statistics.totalSoLong)}
                                    icon={Box}
                                    color="pink"
                                />
                                <StatisticsCard
                                    title="Tổng khối lượng"
                                    value={`${formatNumber(statistics.totalKhoiLuong)} KG`}
                                    icon={TrendingUp}
                                    color="green"
                                />
                                <StatisticsCard
                                    title="Số tổ"
                                    value={statistics.countTo}
                                    icon={Users}
                                    color="purple"
                                />
                            </div>
                        </>
                    )}

                    {viewTab === 'chuyen' && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <StatisticsCard
                                    title="Tổng số lồng chuyển"
                                    value={formatNumberWithDecimal(statistics.totalSoLongChuyen)}
                                    icon={ArrowRightLeft}
                                    color="orange"
                                />
                                <StatisticsCard
                                    title="Tổng khối lượng"
                                    value={`${formatNumber(statistics.totalKhoiLuong)} KG`}
                                    icon={TrendingUp}
                                    color="green"
                                />
                                <StatisticsCard
                                    title="Tổng số lồng nhận"
                                    value={formatNumberWithDecimal(statistics.totalSoLongNhan)}
                                    icon={Box}
                                    color="pink"
                                />
                            </div>
                        </>
                    )}

                    {viewTab === 'pallet' && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <StatisticsCard
                                    title="Tổng đóng"
                                    value={`${formatNumber(statistics.totalDong)} Cái`}
                                    icon={Package}
                                    color="teal"
                                />
                                <StatisticsCard
                                    title="Tổng sửa"
                                    value={`${formatNumber(statistics.totalSua)} Cái`}
                                    icon={Layers}
                                    color="orange"
                                />
                                <StatisticsCard
                                    title="Tổng"
                                    value={`${formatNumber(statistics.total)} Cái`}
                                    icon={TrendingUp}
                                    color="green"
                                />
                            </div>
                        </>
                    )}

                    {/* Filters */}
                    {showFilters && (
                        <div className="mb-4 p-4 border rounded-lg bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                {/* Search */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tìm kiếm
                                    </label>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={e => {
                                                setSearch(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Nhập từ khóa..."
                                        />
                                        {search && (
                                            <button
                                                onClick={() => setSearch('')}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Multi-select TỔ */}
                                <div ref={toDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Lọc theo Tổ
                                    </label>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowToDropdown(v => !v)}
                                            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
                                        >
                                            <span className="truncate text-left">
                                                {selectedToList.length === 0
                                                    ? 'Tất cả tổ'
                                                    : `Đã chọn ${selectedToList.length} tổ`}
                                            </span>
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        </button>
                                        {showToDropdown && (
                                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
                                                <div
                                                    className="px-3 py-2 text-xs text-gray-500 cursor-pointer hover:bg-gray-50 border-b"
                                                    onClick={() => setSelectedToList([])}
                                                >
                                                    Chọn tất cả / Bỏ chọn
                                                </div>
                                                {toOptions.map(to => {
                                                    const checked = selectedToList.includes(to);
                                                    return (
                                                        <div
                                                            key={to}
                                                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                            onClick={() => toggleToOption(to)}
                                                        >
                                                            <div className={`w-4 h-4 mr-2 border rounded-sm flex items-center justify-center ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                                {checked && <Check className="w-3 h-3 text-white" />}
                                                            </div>
                                                            <span>{to}</span>
                                                        </div>
                                                    );
                                                })}
                                                {toOptions.length === 0 && (
                                                    <div className="px-3 py-2 text-xs text-gray-400">
                                                        Không có dữ liệu Tổ
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Multi-select NGHIỆP VỤ (chỉ hiện khi tab Pallet) */}
                                {viewTab === 'pallet' && (
                                    <div ref={nghiepVuDropdownRef}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Lọc theo Nghiệp vụ
                                        </label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowNghiepVuDropdown(v => !v)}
                                                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
                                            >
                                                <span className="truncate text-left">
                                                    {selectedNghiepVuList.length === 0
                                                        ? 'Tất cả nghiệp vụ'
                                                        : `Đã chọn ${selectedNghiepVuList.length}`}
                                                </span>
                                                <ChevronDown className="w-4 h-4 text-gray-400" />
                                            </button>
                                            {showNghiepVuDropdown && (
                                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
                                                    <div
                                                        className="px-3 py-2 text-xs text-gray-500 cursor-pointer hover:bg-gray-50 border-b"
                                                        onClick={() => setSelectedNghiepVuList([])}
                                                    >
                                                        Chọn tất cả / Bỏ chọn
                                                    </div>
                                                    {nghiepVuOptions.map(nv => {
                                                        const checked = selectedNghiepVuList.includes(nv);
                                                        return (
                                                            <div
                                                                key={nv}
                                                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                                onClick={() => toggleNghiepVuOption(nv)}
                                                            >
                                                                <div className={`w-4 h-4 mr-2 border rounded-sm flex items-center justify-center ${checked ? 'bg-teal-600 border-teal-600' : 'border-gray-300'}`}>
                                                                    {checked && <Check className="w-3 h-3 text-white" />}
                                                                </div>
                                                                <span>{nv}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    {nghiepVuOptions.length === 0 && (
                                                        <div className="px-3 py-2 text-xs text-gray-400">
                                                            Không có dữ liệu Nghiệp vụ
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Từ ngày */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Từ ngày
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="date"
                                            value={filters.startDate || ''}
                                            onChange={(e) => handleFilterDateChange('startDate', e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>

                                {/* Đến ngày */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Đến ngày
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="date"
                                            value={filters.endDate || ''}
                                            onChange={(e) => handleFilterDateChange('endDate', e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center">
                                <button
                                    onClick={clearFilters}
                                    className="px-3 py-1.5 text-xs md:text-sm text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100"
                                >
                                    Xóa bộ lọc
                                </button>
                                <div className="text-xs md:text-sm text-gray-600">
                                    Tổng bản ghi:{' '}
                                    <span className="font-semibold">{filteredData.length}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bảng chi tiết */}
                    <div className="overflow-x-auto -mx-4 md:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden border border-gray-200 rounded-lg">
                                {viewTab === 'pallet' ? (
                                    // Bảng cho tab Pallet
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('NGÀY')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Ngày
                                                        <SortIcon sortConfig={sortConfig} column="NGÀY" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('TỔ')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Tổ
                                                        <SortIcon sortConfig={sortConfig} column="TỔ" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('TÊN HÀNG')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Tên hàng
                                                        <SortIcon sortConfig={sortConfig} column="TÊN HÀNG" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('NGHIỆP VỤ')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Nghiệp vụ
                                                        <SortIcon sortConfig={sortConfig} column="NGHIỆP VỤ" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('KHỐI LƯỢNG (KG)')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Khối lượng (Cái)
                                                        <SortIcon sortConfig={sortConfig} column="KHỐI LƯỢNG (KG)" />
                                                    </div>
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                    Thao tác
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentItems.length > 0 ? (
                                                currentItems.map(row => (
                                                    <tr key={row.ID_CT} className="hover:bg-gray-50">
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                            {row['NGÀY']
                                                                ? new Date(row['NGÀY']).toLocaleDateString('vi-VN')
                                                                : ''}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-teal-700">
                                                            {row['TỔ']}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                            {row['TÊN HÀNG']}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                            {row['NGHIỆP VỤ'] || '-'}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-teal-700">
                                                            {formatNumber(row['KHỐI LƯỢNG (KG)'])}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-center">
                                                            <button
                                                                onClick={() => handleDeletePallet(row)}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                                title="Xóa"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={6}
                                                        className="px-4 py-6 text-center text-sm text-gray-500"
                                                    >
                                                        Không có bản ghi nào phù hợp.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                ) : viewTab === 'chuyen' ? (
                                    // Bảng cho tab Chuyển anh sang em
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('NGÀY')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Ngày
                                                        <SortIcon sortConfig={sortConfig} column="NGÀY" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('TỔ CHUYỂN')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Tổ chuyển
                                                        <SortIcon sortConfig={sortConfig} column="TỔ CHUYỂN" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('TỔ NHẬN')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Tổ nhận
                                                        <SortIcon sortConfig={sortConfig} column="TỔ NHẬN" />
                                                    </div>
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">
                                                    Số lồng
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('KHỐI LƯỢNG/LỒNG CD')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        KL/Lồng CD (KG)
                                                        <SortIcon sortConfig={sortConfig} column="KHỐI LƯỢNG/LỒNG CD" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('KHỐI LƯỢNG')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Khối lượng (KG)
                                                        <SortIcon sortConfig={sortConfig} column="KHỐI LƯỢNG" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('KHỐI LƯỢNG/LỒNG EM')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        KL/Lồng Em (KG)
                                                        <SortIcon sortConfig={sortConfig} column="KHỐI LƯỢNG/LỒNG EM" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('SỐ LỒNG QUY ĐỔI')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Số lồng quy đổi
                                                        <SortIcon sortConfig={sortConfig} column="SỐ LỒNG QUY ĐỔI" />
                                                    </div>
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">
                                                    Ghi chú
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                    Thao tác
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentItems.length > 0 ? (
                                                currentItems.map(row => (
                                                    <tr key={row.IDCBB} className="hover:bg-gray-50">
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                            {row['NGÀY']
                                                                ? new Date(row['NGÀY']).toLocaleDateString('vi-VN')
                                                                : ''}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-indigo-700">
                                                            {row['TỔ CHUYỂN']}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-pink-600">
                                                            {row['TỔ NHẬN']}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600">
                                                            {formatNumberWithDecimal(row['SỐ LỒNG'])}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-purple-600">
                                                            {formatNumber(row['KHỐI LƯỢNG/LỒNG CD'])}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-green-700">
                                                            {formatNumber(row['KHỐI LƯỢNG'])}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600">
                                                            {formatNumber(row['KHỐI LƯỢNG/LỒNG EM'])}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-orange-600">
                                                            {formatNumberWithDecimal(row['SỐ LỒNG QUY ĐỔI'])}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-600">
                                                            {row['GHI CHÚ'] || '-'}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-center">
                                                            <button
                                                                onClick={() => handleDeleteChuyenBaoBi(row)}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                                title="Xóa"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={10}
                                                        className="px-4 py-6 text-center text-sm text-gray-500"
                                                    >
                                                        Không có bản ghi nào phù hợp.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                ) : (
                                    // Bảng cho tab Anh và Em
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('NGÀY')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Ngày
                                                        <SortIcon sortConfig={sortConfig} column="NGÀY" />
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('TỔ')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Tổ
                                                        <SortIcon sortConfig={sortConfig} column="TỔ" />
                                                    </div>
                                                </th>

                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">
                                                    Tên hàng
                                                </th>

                                                {viewTab === 'anh' ? (
                                                    <>
                                                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                            Số dây
                                                        </th>
                                                    </>
                                                ) : (
                                                    <>
                                                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                            Số lồng
                                                        </th>
                                                        <th
                                                            className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 select-none"
                                                            onClick={() => handleSort('KHỐI LƯỢNG THEO LỒNG')}
                                                        >
                                                            Khối lượng theo lồng (KG)
                                                        </th>
                                                    </>
                                                )}

                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('KHỐI LƯỢNG')}
                                                >
                                                    Khối lượng (KG)
                                                </th>

                                                {viewTab === 'anh' && (
                                                    <>
                                                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">
                                                            Số xe
                                                        </th>
                                                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">
                                                            NCC
                                                        </th>
                                                    </>
                                                )}
                                                <th
                                                    className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('type')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Loại
                                                        <SortIcon sortConfig={sortConfig} column="type" />
                                                    </div>
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                    Thao tác
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentItems.length > 0 ? (
                                                currentItems.map(row => {
                                                    const nhap = nhapBaoBiMap[row.ID_NHAPBAOBI] || {};
                                                    const isChuyenTru = row.type === 'chuyen_tru';
                                                    const isChuyenCong = row.type === 'chuyen_cong';
                                                    const khoiLuong = parseNumber(row['KHỐI LƯỢNG']);
                                                    return (
                                                        <tr
                                                            key={row.IDPB}
                                                            className={`hover:bg-gray-50 ${isChuyenTru ? 'bg-red-50' :
                                                                isChuyenCong ? 'bg-green-50' : ''
                                                                }`}
                                                        >
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                                {row['NGÀY']
                                                                    ? new Date(row['NGÀY']).toLocaleDateString('vi-VN')
                                                                    : ''}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-indigo-700">
                                                                {row['TỔ']}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                                {row['TÊN HÀNG']}
                                                            </td>

                                                            {viewTab === 'anh' ? (
                                                                <>
                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center text-blue-600">
                                                                        {row['SỐ DÂY'] || '-'}
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center text-blue-600">
                                                                        {formatNumberWithDecimal(row['SỐ LỒNG'])}
                                                                    </td>
                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center text-green-700">
                                                                        {formatNumber(row['KHỐI LƯỢNG THEO LỒNG'])}
                                                                    </td>
                                                                </>
                                                            )}

                                                            <td className={`px-4 py-2 whitespace-nowrap text-sm text-center font-semibold ${isChuyenTru ? 'text-red-700' :
                                                                isChuyenCong ? 'text-green-700' :
                                                                    'text-green-700'
                                                                }`}>
                                                                {isChuyenTru && '- '}
                                                                {isChuyenCong && '+ '}
                                                                {formatNumber(Math.abs(khoiLuong))}
                                                            </td>

                                                            {viewTab === 'anh' && (
                                                                <>
                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                                        {isChuyenTru || isChuyenCong ? '-' : (nhap['SỐ XE'] || '')}
                                                                    </td>
                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                                                        {isChuyenTru || isChuyenCong ? '-' : (nhap['NCC'] || '')}
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                                {isChuyenTru && (
                                                                    <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                                                                        Chuyển đi
                                                                    </span>
                                                                )}
                                                                {isChuyenCong && (
                                                                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                                                                        Nhận về
                                                                    </span>
                                                                )}
                                                                {!isChuyenTru && !isChuyenCong && (
                                                                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                                                        Phân bổ
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-center">
                                                                {/* Chỉ cho phép xóa loại "phanbo", không cho xóa "chuyen_tru" hoặc "chuyen_cong" */}
                                                                {row.type === 'phanbo' ? (
                                                                    <button
                                                                        onClick={() => handleDeletePhanBo(row)}
                                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                                        title="Xóa phân bổ"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={viewTab === 'anh' ? 10 : 7}
                                                        className="px-4 py-6 text-center text-sm text-gray-500"
                                                    >
                                                        Không có bản ghi nào phù hợp.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>

            {/* Modal Phân bổ bao bì em */}
            {showPhanBoEmModal && (
                <PhanBoBaoBiEmTongThe
                    packagingData={Object.values(nhapBaoBiMap)}
                    phanBoData={combinedData.filter(row => row.loai === 'em')}
                    onClose={() => setShowPhanBoEmModal(false)}
                    onSuccess={async () => {
                        // Fetch lại dữ liệu sau khi phân bổ thành công
                        try {
                            const [phanBoRes, chuyenRes] = await Promise.all([
                                authUtils.apiRequest('PHANBOBAOBI', 'Find', {}),
                                authUtils.apiRequest('CHUYENBAOBI', 'Find', {}),
                            ]);
                            setPhanBoData(phanBoRes || []);
                            setChuyenBaoBiData(chuyenRes || []);
                            toast.success('Phân bổ bao bì em thành công!');
                        } catch (err) {
                            console.error('Error refetching data:', err);
                        }
                        setShowPhanBoEmModal(false);
                    }}
                />
            )}

            {/* Modal Chuyển bao bì anh sang em */}
            {showChuyenBaoBiModal && (
                <ChuyenBaoBiAnhSangEm
                    phanBoData={combinedData}
                    chuyenBaoBiData={chuyenBaoBiData}
                    onClose={() => setShowChuyenBaoBiModal(false)}
                    onSuccess={async () => {
                        // Fetch lại dữ liệu sau khi chuyển thành công
                        try {
                            const [phanBoRes, chuyenRes] = await Promise.all([
                                authUtils.apiRequest('PHANBOBAOBI', 'Find', {}),
                                authUtils.apiRequest('CHUYENBAOBI', 'Find', {}),
                            ]);
                            setPhanBoData(phanBoRes || []);
                            setChuyenBaoBiData(chuyenRes || []);
                            toast.success('Chuyển bao bì thành công!');
                        } catch (err) {
                            console.error('Error refetching data:', err);
                        }
                        setShowChuyenBaoBiModal(false);
                    }}
                />
            )}

            {/* Modal Nhập dữ liệu Pallet */}
            {showNhapPalletModal && (
                <NhapPallet
                    onClose={() => setShowNhapPalletModal(false)}
                    onSuccess={async () => {
                        // Fetch lại dữ liệu sau khi nhập thành công
                        try {
                            const palletRes = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});
                            setPalletData(palletRes || []);
                            toast.success('Nhập dữ liệu pallet thành công!');
                        } catch (err) {
                            console.error('Error refetching pallet data:', err);
                        }
                        setShowNhapPalletModal(false);
                    }}
                />
            )}

            {/* Modal Confirm Xóa */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full">
                                <AlertCircle className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">{confirmTitle}</h3>
                        </div>
                        <p className="text-gray-600 mb-6 whitespace-pre-line">{confirmMessage}</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setConfirmAction(null);
                                }}
                                disabled={isDeleting}
                                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={() => confirmAction && confirmAction()}
                                disabled={isDeleting}
                                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Đang xóa...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Xóa
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default PhanBoBaoBiManagement;

